import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useAuthStore } from '@/stores';
import { PageLoader } from '@/components/ui';
import { E2E_BYPASS_ACCOUNT, hasE2eBypassSession } from './e2eAuthBypass';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const zustandIsAuthenticated = useAuthStore(state => state.isAuthenticated);
  const msalIsAuthenticated = useIsAuthenticated();
  const { accounts, inProgress } = useMsal();
  const { setMsalAuthentication } = useAuthStore();
  const location = useLocation();

  // E2E only: with VITE_E2E_AUTH_BYPASS on, a live MPN session written by
  // e2e/auth.setup.ts (dev god token) counts as signed in — that browser has
  // no MSAL account. Off (the default) this is always false.
  const bypassSession = hasE2eBypassSession();
  useEffect(() => {
    if (bypassSession && !msalIsAuthenticated && !zustandIsAuthenticated) {
      setMsalAuthentication(E2E_BYPASS_ACCOUNT);
    }
  }, [
    bypassSession,
    msalIsAuthenticated,
    zustandIsAuthenticated,
    setMsalAuthentication,
  ]);

  // Sync MSAL authentication state with Zustand store
  useEffect(() => {
    if (msalIsAuthenticated && accounts.length > 0 && !zustandIsAuthenticated) {
      const account = accounts[0];
      if (account) {
        // User is authenticated in MSAL but not in Zustand, sync the state
        setMsalAuthentication(account);
      }
    }
  }, [
    msalIsAuthenticated,
    accounts,
    zustandIsAuthenticated,
    setMsalAuthentication,
  ]);

  // Wait for MSAL to finish any in-progress operations before making auth decisions
  // This prevents false redirects to login during page refresh
  if (inProgress !== InteractionStatus.None) {
    return <PageLoader />;
  }

  // Check authentication from either MSAL or Zustand (for backward compatibility)
  const isAuthenticated =
    msalIsAuthenticated || zustandIsAuthenticated || bypassSession;

  if (!isAuthenticated) {
    // Preserve the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
